import { CartCache } from '@theme/cart-cache';
import { CartStore } from '@theme/cart-store';
import { CartAPI } from '@theme/cart-api';
import { bindCartForms } from '@theme/cart-bind-forms';
import { bindQuickAddButtons } from '@theme/cart-bind-quick-add';

export function updateCartCounters(cart) {
  const itemCount = cart.item_count || 0;

  document.querySelectorAll('[data-cart-count]').forEach((el) => {
    el.textContent = itemCount;
    el.style.display = itemCount > 0 ? '' : 'none';
  });

  document.querySelectorAll('.cart-count__text, .chm-cart-count, .cart-indicator-fixed-count').forEach((el) => {
    el.textContent = itemCount;
    el.classList.remove('cart-indicator-pulse');
    void el.offsetWidth;
    el.classList.add('cart-indicator-pulse');
  });

  document.querySelectorAll('.cart-bubble, .bottom-nav .cart-bubble').forEach((bubble) => {
    const countEl = bubble.querySelector('.cart-bubble__text-count, .cart-bubble__count');
    if (countEl) {
      countEl.textContent = itemCount < 100 ? String(itemCount) : '99+';
    }
    bubble.style.display = itemCount > 0 ? 'grid' : 'none';
    bubble.classList.toggle('visually-hidden', itemCount === 0);
  });

  CartCache.set(cart);

  sessionStorage.setItem('cart-count', JSON.stringify({
    value: String(itemCount),
    timestamp: Date.now(),
  }));
}

export async function updateCartDrawer() {
  const drawer = document.querySelector('#CartDrawer, [id*="cart-drawer"], .cart-drawer');
  if (!drawer) return;

  try {
    const sectionId = drawer.dataset.sectionId || 'cart-drawer';
    const response = await fetch(`/?section_id=${sectionId}`);
    if (!response.ok) return;

    const html = await response.text();
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const newDrawer = doc.querySelector(`#${drawer.id}, [id*="cart-drawer"], .cart-drawer`);

    if (newDrawer) {
      drawer.replaceWith(newDrawer);
      if (window.initCartDrawer) {
        window.initCartDrawer();
      }
      bindUI(newDrawer);
    }
  } catch (error) {
    console.error('Failed to update cart drawer:', error);
  }
}

export function dispatchCartEvents(cart, variantId = null) {
  const eventDetail = {
    cart,
    item_count: cart.item_count || 0,
    items: cart.items || [],
    variantId: variantId ? String(variantId) : null,
  };

  document.dispatchEvent(new CustomEvent('cart:updated', {
    bubbles: true,
    cancelable: false,
    detail: eventDetail,
  }));

  document.dispatchEvent(new CustomEvent('cart:update', {
    bubbles: true,
    detail: {
      cart,
      item_count: cart.item_count || 0,
      data: { itemCount: cart.item_count || 0 },
    },
  }));

  document.dispatchEvent(new CustomEvent('shopify:cart:update', {
    bubbles: true,
    detail: {
      cart,
      item_count: cart.item_count || 0,
    },
  }));
}

export function bindUI(root = document) {
  bindCartForms(root);
  bindQuickAddButtons(root);
}

export function updateQuickAddButtons(state) {
  document.querySelectorAll('[data-quick-add-button]').forEach((quickAddButton) => {
    const wrapper = quickAddButton.closest('.quick-add-button-wrapper');
    if (!wrapper) return;

    let variantId = wrapper.dataset.variantId || wrapper.dataset.variantid || wrapper.getAttribute('data-variant-id');
    if (!variantId) return;
    variantId = String(variantId);

    const qty = state.items[variantId] || 0;

    const num = quickAddButton.querySelector('[data-quick-add-count]');
    const counterEl = quickAddButton.querySelector('[data-quick-add-counter]');
    const circle = quickAddButton.querySelector('[data-quick-add-circle]');
    const loader = quickAddButton.querySelector('.quick-add-button__loader');
    const circleLoader = quickAddButton.querySelector('.quick-add-button__circle-loader');
    const plus = quickAddButton.querySelector('[data-quick-add-plus]');
    const minus = quickAddButton.querySelector('[data-quick-add-minus]');

    if (num) {
      num.textContent = qty;
      if (loader) {
        loader.style.display = 'none';
        loader.style.visibility = 'hidden';
        num.style.visibility = 'visible';
        num.style.position = '';
      }
    }
    if (counterEl) {
      if (qty > 0) {
        counterEl.classList.add('is-visible');
        counterEl.style.display = 'flex';
      } else {
        counterEl.classList.remove('is-visible');
        counterEl.style.display = 'none';
      }
    }
    if (qty > 0) {
      quickAddButton.classList.add('is-expanded');
    } else {
      quickAddButton.classList.remove('is-expanded');
    }
    quickAddButton.classList.remove('is-loading');

    if (plus) plus.disabled = false;
    if (minus) minus.disabled = false;

    if (circle) {
      if (circleLoader) {
        circleLoader.style.display = 'none';
        circleLoader.style.visibility = 'hidden';
      }
      const icon = circle.querySelector('img');
      if (!icon) return;
      if (qty === 0) {
        icon.style.visibility = 'visible';
        icon.style.position = '';
      } else {
        icon.style.visibility = 'hidden';
        icon.style.position = 'absolute';
      }
    }
  });
}

export function initMobileBar() {
  const bar = document.querySelector('[data-mobile-bar]');
  if (!bar) return;

  const isMobile = window.innerWidth < 750;
  if (isMobile) {
    requestAnimationFrame(() => {
      bar.classList.add('visible');
    });
  }

  window.addEventListener('resize', () => {
    const isMobileNow = window.innerWidth < 750;
    if (isMobileNow && !bar.classList.contains('visible')) {
      bar.classList.add('visible');
    } else if (!isMobileNow && bar.classList.contains('visible')) {
      bar.classList.remove('visible');
    }
  });
}

export async function initCart(root = document) {
  bindUI(root);
  initMobileBar();

  try {
    const cart = await CartAPI.fetch();
    CartStore.set(cart);
  } catch (error) {
    console.error('Failed to load cart on init:', error);
  }
}
